/**
 * Project Facts Step Component
 * Phase 1 of the new AI workflow - Collects user-provided project facts
 * This component ONLY handles factual information - no AI hallucination risk
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// Zod validation schema
const projectFactsSchema = z.object({
  projectName: z.string()
    .min(3, 'Project name must be at least 3 characters')
    .max(100, 'Project name must be less than 100 characters'),
  projectAddress: z.string()
    .min(10, 'Please enter a complete address')
    .max(200, 'Address must be less than 200 characters'),
  projectType: z.string()
    .min(1, 'Please select a project type'),
  squareFootage: z.string()
    .optional()
    .refine((val) => !val || !isNaN(Number(val)), 'Must be a number')
    .refine((val) => !val || Number(val) > 0, 'Must be greater than 0'),
  startDate: z.string()
    .min(1, 'Start date is required')
    .refine((date) => {
      const d = new Date(date);
      return !isNaN(d.getTime());
    }, 'Please enter a valid date'),
  completionDate: z.string()
    .min(1, 'Completion date is required')
    .refine((date) => {
      const d = new Date(date);
      return !isNaN(d.getTime());
    }, 'Please enter a valid date'),
  contactName: z.string()
    .min(2, 'Please enter your name')
    .max(100, 'Name must be less than 100 characters'),
  contactEmail: z.string()
    .email('Please enter a valid email address'),
  contactPhone: z.string()
    .regex(/^\d{3}-\d{3}-\d{4}$/, 'Please use format: 555-555-5555'),
  companyName: z.string()
    .max(100, 'Company name must be less than 100 characters')
    .optional(),
}).refine((data) => {
  const start = new Date(data.startDate);
  const end = new Date(data.completionDate);
  return end > start;
}, {
  message: 'Completion date must be after start date',
  path: ['completionDate'],
});

export type ProjectFactsData = z.infer<typeof projectFactsSchema>;

interface ProjectFactsStepProps {
  onNext: (data: ProjectFactsData) => void;
  onCancel?: () => void;
  initialData?: Partial<ProjectFactsData>;
}

const PROJECT_TYPES = [
  { value: 'residential-new', label: 'Residential - New Construction' },
  { value: 'residential-remodel', label: 'Residential - Remodel/Addition' },
  { value: 'commercial-new', label: 'Commercial - New Construction' },
  { value: 'commercial-ti', label: 'Commercial - Tenant Improvement' },
  { value: 'commercial-demo', label: 'Commercial - Demolition' },
  { value: 'industrial-new', label: 'Industrial - New Construction' },
  { value: 'other', label: 'Other' },
];

// Helper function to format phone number
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

// Helper function to get default dates
function getDefaultDates() {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + 90);

  return {
    startDate: today.toISOString().split('T')[0],
    completionDate: futureDate.toISOString().split('T')[0],
  };
}

export function ProjectFactsStep({ onNext, onCancel, initialData }: ProjectFactsStepProps) {
  const defaultDates = getDefaultDates();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ProjectFactsData>({
    resolver: zodResolver(projectFactsSchema),
    defaultValues: {
      ...defaultDates,
      ...initialData,
    },
  });

  // Handle phone number formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue('contactPhone', formatted, { shouldValidate: true });
  };

  const onSubmit = (data: ProjectFactsData) => {
    onNext(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-semibold text-neutral-900">Project Details</h2>
          <span className="text-sm text-neutral-500">Step 1 of 3</span>
        </div>
        <p className="text-neutral-600">
          First, we need some basic information about your project. This will take about 60 seconds.
        </p>
      </div>

      {/* Section 1: Project Basics */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-white text-sm font-semibold">
            1
          </span>
          <h3 className="text-lg font-semibold text-neutral-900">Project Basics</h3>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Project Name */}
          <div className="md:col-span-2">
            <label htmlFor="projectName" className="block text-sm font-medium text-neutral-700 mb-1">
              Project Name <span className="text-red-600">*</span>
            </label>
            <input
              id="projectName"
              type="text"
              placeholder="e.g., Smith Residence Remodel"
              {...register('projectName')}
              className={`input-field ${errors.projectName ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              aria-describedby={errors.projectName ? 'projectName-error' : undefined}
            />
            {errors.projectName && (
              <p id="projectName-error" className="text-sm text-red-600 mt-1" role="alert">
                {errors.projectName.message}
              </p>
            )}
          </div>

          {/* Project Type */}
          <div className="md:col-span-2">
            <label htmlFor="projectType" className="block text-sm font-medium text-neutral-700 mb-1">
              Project Type <span className="text-red-600">*</span>
            </label>
            <select
              id="projectType"
              {...register('projectType')}
              className={`input-field ${errors.projectType ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              aria-describedby={errors.projectType ? 'projectType-error' : undefined}
            >
              <option value="">Select project type...</option>
              {PROJECT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.projectType && (
              <p id="projectType-error" className="text-sm text-red-600 mt-1" role="alert">
                {errors.projectType.message}
              </p>
            )}
          </div>

          {/* Project Address */}
          <div className="md:col-span-2">
            <label htmlFor="projectAddress" className="block text-sm font-medium text-neutral-700 mb-1">
              Project Address <span className="text-red-600">*</span>
            </label>
            <input
              id="projectAddress"
              type="text"
              placeholder="e.g., 123 Main St, San Luis Obispo, CA 93401"
              {...register('projectAddress')}
              className={`input-field ${errors.projectAddress ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              aria-describedby={errors.projectAddress ? 'projectAddress-error' : undefined}
            />
            {errors.projectAddress && (
              <p id="projectAddress-error" className="text-sm text-red-600 mt-1" role="alert">
                {errors.projectAddress.message}
              </p>
            )}
          </div>

          {/* Square Footage */}
          <div>
            <label htmlFor="squareFootage" className="block text-sm font-medium text-neutral-700 mb-1">
              Total Square Footage (Optional)
            </label>
            <input
              id="squareFootage"
              type="text"
              inputMode="numeric"
              placeholder="e.g., 2500"
              {...register('squareFootage')}
              className={`input-field ${errors.squareFootage ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              aria-describedby={errors.squareFootage ? 'squareFootage-error' : undefined}
            />
            {errors.squareFootage && (
              <p id="squareFootage-error" className="text-sm text-red-600 mt-1" role="alert">
                {errors.squareFootage.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Section 2: Project Timeline */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-white text-sm font-semibold">
            2
          </span>
          <h3 className="text-lg font-semibold text-neutral-900">Project Timeline</h3>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Start Date */}
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-neutral-700 mb-1">
              Estimated Start Date <span className="text-red-600">*</span>
            </label>
            <input
              id="startDate"
              type="date"
              {...register('startDate')}
              className={`input-field ${errors.startDate ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              aria-describedby={errors.startDate ? 'startDate-error' : undefined}
            />
            {errors.startDate && (
              <p id="startDate-error" className="text-sm text-red-600 mt-1" role="alert">
                {errors.startDate.message}
              </p>
            )}
          </div>

          {/* Completion Date */}
          <div>
            <label htmlFor="completionDate" className="block text-sm font-medium text-neutral-700 mb-1">
              Estimated Completion Date <span className="text-red-600">*</span>
            </label>
            <input
              id="completionDate"
              type="date"
              {...register('completionDate')}
              className={`input-field ${errors.completionDate ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              aria-describedby={errors.completionDate ? 'completionDate-error' : undefined}
            />
            {errors.completionDate && (
              <p id="completionDate-error" className="text-sm text-red-600 mt-1" role="alert">
                {errors.completionDate.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Section 3: Contact Information */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-3">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-white text-sm font-semibold">
            3
          </span>
          <h3 className="text-lg font-semibold text-neutral-900">Contact Information</h3>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Contact Name */}
          <div>
            <label htmlFor="contactName" className="block text-sm font-medium text-neutral-700 mb-1">
              Your Name <span className="text-red-600">*</span>
            </label>
            <input
              id="contactName"
              type="text"
              placeholder="e.g., John Smith"
              {...register('contactName')}
              className={`input-field ${errors.contactName ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              aria-describedby={errors.contactName ? 'contactName-error' : undefined}
            />
            {errors.contactName && (
              <p id="contactName-error" className="text-sm text-red-600 mt-1" role="alert">
                {errors.contactName.message}
              </p>
            )}
          </div>

          {/* Contact Email */}
          <div>
            <label htmlFor="contactEmail" className="block text-sm font-medium text-neutral-700 mb-1">
              Your Email <span className="text-red-600">*</span>
            </label>
            <input
              id="contactEmail"
              type="email"
              placeholder="e.g., john@example.com"
              {...register('contactEmail')}
              className={`input-field ${errors.contactEmail ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              aria-describedby={errors.contactEmail ? 'contactEmail-error' : 'contactEmail-hint'}
            />
            <p id="contactEmail-hint" className="text-sm text-neutral-500 mt-1">
              We&apos;ll send your plan access link to this email
            </p>
            {errors.contactEmail && (
              <p id="contactEmail-error" className="text-sm text-red-600 mt-1" role="alert">
                {errors.contactEmail.message}
              </p>
            )}
          </div>

          {/* Contact Phone */}
          <div>
            <label htmlFor="contactPhone" className="block text-sm font-medium text-neutral-700 mb-1">
              Your Phone <span className="text-red-600">*</span>
            </label>
            <input
              id="contactPhone"
              type="tel"
              placeholder="555-555-5555"
              {...register('contactPhone')}
              onChange={handlePhoneChange}
              className={`input-field ${errors.contactPhone ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              aria-describedby={errors.contactPhone ? 'contactPhone-error' : undefined}
              maxLength={12}
            />
            {errors.contactPhone && (
              <p id="contactPhone-error" className="text-sm text-red-600 mt-1" role="alert">
                {errors.contactPhone.message}
              </p>
            )}
          </div>

          {/* Company Name */}
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-neutral-700 mb-1">
              Company Name (Optional)
            </label>
            <input
              id="companyName"
              type="text"
              placeholder="e.g., ABC Construction"
              {...register('companyName')}
              className={`input-field ${errors.companyName ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
              aria-describedby={errors.companyName ? 'companyName-error' : undefined}
            />
            {errors.companyName && (
              <p id="companyName-error" className="text-sm text-red-600 mt-1" role="alert">
                {errors.companyName.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-neutral-200">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary sm:w-auto order-2 sm:order-1"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn-primary flex-1 sm:flex-initial order-1 sm:order-2 flex items-center justify-center gap-2"
        >
          Continue to Waste Analysis
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </form>
  );
}
